import {
  Component,
  OnDestroy,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.service';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs/operators';
import { questionTypes } from './question-types';
import { getQuestionsState } from '../reducers';
import { Subscription } from 'rxjs';
import { questionSuggestions } from './question-suggestions';
import {
  ProcessQuestionModel,
  ProcessQuestionTypeModel,
} from '../models/questions.model';
import { v4 as uuid } from 'uuid';
import {
  CleanQuestionsState,
  LoadQuestions,
  RemoveQuestion,
  UpdateQuestion,
} from '../actions/questions.actions';
import { ModalComponent } from '../../lib/services/modal.service';
import { ProcessStepModel } from '../../jobs-module/edit-job-tpl-process/process.model';
import { QuestionState } from '../reducers/questions.reducers';
import { SingleQuestionComponent } from './single-question/single-question.component';

@Component({
  selector: 'mj-add-question',
  templateUrl: './add-question.component.html',
  styleUrls: ['./add-question.component.scss'],
})
export class AddQuestionComponent extends ModalComponent
  implements OnInit, OnDestroy {
  @ViewChildren(SingleQuestionComponent) childrenQuestions!: QueryList<
    SingleQuestionComponent
  >;

  questionSuggestions = [];

  questionTypes = questionTypes;
  private selectedQuestion$: Subscription;
  state: QuestionState;

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private snackBar: MatSnackBar
  ) {
    super();
    this.data.pipe(take(1)).subscribe((data: ProcessStepModel) => {
      if (data != null) {
        store.dispatch(
          new LoadQuestions({
            questions: data.questions.map(item =>
              Object.assign(item, { uuid: uuid() })
            ),
            step_type: data.type,
            step_id: data.id,
          })
        );
      }
    });
  }

  ngOnInit() {
    this.selectedQuestion$ = this.store
      .select(getQuestionsState)
      .subscribe(questionsState => {
        if (!questionsState) {
          return;
        }
        const previousState = this.state;
        this.state = questionsState;
        if (
          !previousState ||
          previousState.step_type !== this.state.step_type
        ) {
          this.prepareQuestionSuggestions(this.state.step_type);
          if (this.state.step_type !== 4) {
            this.questionTypes = questionTypes.filter(
              type => type.type !== ProcessQuestionTypeModel.OneWayVideo
            );
          }
        }
      });
  }

  ngOnDestroy(): void {
    if (this.selectedQuestion$) {
      this.selectedQuestion$.unsubscribe();
    }
  }

  onDoneClicked() {
    const areAllQuestionsValid = this.state.questions
      .map(q => this._validateAnswers(q))
      .every(this._isQuestionValid);
    if (!areAllQuestionsValid) {
      this.translate
        .get('job_questions_snackbar_mc_none_selected')
        .pipe(take(1))
        .subscribe(text => {
          this.snackBar.open(text, null, {
            duration: 3000,
          });
        });
      return;
    }
    this.resolve.next(this.state.questions);
    this.store.dispatch(new CleanQuestionsState());
  }

  onAddNewQuestion(type?) {
    if (this.state.step_type === 4 && this.state.questions.length > 2) {
      this.translate
        .get('process_cannot_add_3')
        .pipe(take(1))
        .subscribe(msg => {
          return this.snackBar.open(msg, null, {
            duration: 3000,
          });
        });
    }
    const newQuestion: ProcessQuestionModel = {
      debug: null,
      id: null,
      uuid: uuid(),
      question_text: null,
      type:
        this.state.step_type === 4
          ? ProcessQuestionTypeModel.OneWayVideo
          : type,
      reject_on_failure: false,
      is_required: false,
      alternatives: [],
    };

    this.store.dispatch(
      new UpdateQuestion({
        questionToUpdate: newQuestion,
      })
    );
  }

  onRemoveQuestion(index) {
    this.store.dispatch(
      new RemoveQuestion({
        index: index,
      })
    );
  }

  prepareQuestionSuggestions(type: number) {
    this.questionSuggestions = [...questionSuggestions];

    const localLang = localStorage.getItem('language');
    const lang = localLang ? localLang : this.translate.getBrowserLang();

    this.questionSuggestions = this.questionSuggestions.map(question =>
      Object.assign({}, question, {
        question_text:
          lang === 'no'
            ? question.question_text.nor
            : question.question_text.eng,
      })
    );

    this.questionSuggestions.forEach(question => {
      question.alternatives = question.alternatives.map(alt =>
        Object.assign({}, alt, {
          answer_text:
            lang === 'no' ? alt.answer_text.nor : alt.answer_text.eng,
        })
      );
    });

    if (type === 0) {
      this.questionSuggestions = this.questionSuggestions.map(question => {
        if (question.type === ProcessQuestionTypeModel.OneWayVideo) {
          question.type = ProcessQuestionTypeModel.FreeText;
        }
        return question;
      });
    }

    if (type === 4) {
      this.questionSuggestions = this.questionSuggestions.filter(
        question => question.type === ProcessQuestionTypeModel.OneWayVideo
      );
    }
  }

  addThisQuestion(question) {
    if (this.state.step_type === 4 && this.state.questions.length === 3) {
      return this.translate
        .get('application_info_snackbar_cannot_add_more_questions')
        .pipe(take(1))
        .subscribe(res => {
          this.snackBar.open(res, null, {
            duration: 3000,
          });
        });
    }
    this.store.dispatch(
      new UpdateQuestion({
        questionToUpdate: question,
      })
    );
  }

  private _validateAnswers(question): boolean {
    let validationStatus = true;
    let rightAnswers = [];
    if (
      question.type === ProcessQuestionTypeModel.MultiChoice ||
      question.type === ProcessQuestionTypeModel.YesNo
    ) {
      rightAnswers = question.alternatives.filter(answer => answer.is_correct);
      validationStatus = !!rightAnswers.length;
    }
    return validationStatus;
  }

  private _isQuestionValid(question): boolean {
    return question;
  }

  isSuggestionSelected(question) {
    return (
      this.state.questions.findIndex(
        elem => elem.question_text === question.question_text
      ) !== -1
    );
  }

  promptAddingQuestion(elem) {
    this.state.step_type === 4
      ? this.onAddNewQuestion(4)
      : elem.classList.add('selecting');
  }

  closeQuestions() {
    this.childrenQuestions.forEach(question => {
      if (question.isEditable && question.extended) {
        question.assignContainerHeight(true);
      }
    });
  }
}
